import { useTranslation } from 'react-i18next';

import { IndicatorName } from '@/api/rest/resources/types/fact';
import { INDICATOR_DOMAINS } from '@/api/rest/resources/types/IndicatorProduct';
import { DefaultWidgetNames, GraphTileType, WidgetType } from '@/api/rest/resources/types/widget';
import { WidgetSlotRenderer } from '@/components/widgets/WidgetSlotRenderer';
import { useInSituIndicatorConfigurationOrgWidgetsByDomain } from '@/hooks/useInSituIndicatorConfigurationWidgets';
import { useActiveWidgetSlotConfiguration } from '@/hooks/useWidgetConfigurationByOrganizationId';

import { LandPageLayout } from './components/LandPageLayout';

const PAGE_WIDGET_NAME = 'default_template_asset_social';
const PAGE_WIDGET_TYPE = WidgetType.t_page;

export const LandSocialPage = () => {
  const { t } = useTranslation();

  const defaultWidgetConfiguration = useDefaultWidgetConfiguration();
  const customWidgetConfiguration = useActiveWidgetSlotConfiguration(
    PAGE_WIDGET_NAME,
    PAGE_WIDGET_TYPE,
    undefined,
    defaultWidgetConfiguration,
  );
  const widgetConfiguration = customWidgetConfiguration ?? defaultWidgetConfiguration;

  return (
    <LandPageLayout
      signedInPageLayoutProps={{
        customizableWidgetTemplateProps: {
          widgetName: PAGE_WIDGET_NAME,
          widgetType: PAGE_WIDGET_TYPE,
          overrideLayoutConfiguration: defaultWidgetConfiguration,
        },
      }}
      navBarProps={{
        title: t('navbar.social'),
      }}
    >
      <WidgetSlotRenderer nestedWidget={widgetConfiguration} />
    </LandPageLayout>
  );
};

const useDefaultWidgetConfiguration = () => {
  const { t } = useTranslation();
  const inSituWidgets = useInSituIndicatorConfigurationOrgWidgetsByDomain(INDICATOR_DOMAINS.SOCIAL);

  const indicatorHeaderProps = {
    title: t('shared.ncaDetail.details.social.labels.lifeExpectancy'),
    showToggle: false,
    showGraphType: true,
  };

  return {
    name: PAGE_WIDGET_NAME,
    type: PAGE_WIDGET_TYPE,
    widgets: [
      {
        name: 'social_measurement_summary_row',
        type: WidgetType.a_stack,
        visible: true,
        relatedData: {
          direction: 'row',
          center: true,
          spacing: 6,
          className: 'flex-col lg:flex-row lg:flex-wrap items-stretch',
        },
        widgets: [
          {
            name: DefaultWidgetNames.default_fact_card_current_social,
            type: WidgetType.o_fact_summary,
            relatedData: {
              factType: IndicatorName.social_life_expectancy,
              headerText: 'global.widget.currentSocial',
              // TODO: MVP-7534 - add tooltip text when copy is available
              // tooltipText: 'global.widget.currentSocialTooltip',
            },
            visible: true,
          },
          {
            name: DefaultWidgetNames.default_fact_card_historic_social_5y_uplift,
            type: WidgetType.o_fact_summary,
            relatedData: {
              factType: IndicatorName.social_life_expectancy_previous_5y_avg_uplift,
              headerText: 'global.widget.historic5yAvgSocialUplift',
              // TODO: MVP-7534 - add tooltip text when copy is available
              // tooltipText: 'global.widget.historic5yUpliftSocialTooltip',
            },
            visible: true,
          },
          {
            name: DefaultWidgetNames.default_fact_card_projection_social_1y_uplift,
            type: WidgetType.o_fact_summary,
            relatedData: {
              factType: IndicatorName.social_life_expectancy_previous_1y_uplift,
              headerText: 'global.widget.projection1ySocialUplift',
              // TODO: MVP-7534 - add tooltip text when copy is available
              // tooltipText: 'global.widget.projection1yUpliftSocialTooltip',
            },
            visible: true,
          },
        ],
      },
      {
        name: 'measurements_social_indicator_card',
        type: WidgetType.o_details_graph_card,
        visible: true,
        relatedData: {
          headerProps: indicatorHeaderProps,
          graphTileType: GraphTileType.social_life_expectancy,
        },
      },
      ...inSituWidgets,
    ],
  };
};
